export const createRequestDeduper = ({ recentCompletionWindowMs = 0 } = {}) => {
  // WeakMap so scope bookkeeping is released with its scope key (e.g. a discarded store's getState)
  const scopeStateByKey = new WeakMap();

  const getScopeState = scopeKey => {
    if (!scopeStateByKey.has(scopeKey)) {
      scopeStateByKey.set(scopeKey, {
        inFlight: new Map(),
        recentCompletions: new Map(),
      });
    }

    return scopeStateByKey.get(scopeKey);
  };

  // A request skipped by the recent-completion window resolves to undefined, not the previous
  // result — only configure a window where callers ignore the resolved value.
  const runDeduped = async ({ scopeKey, requestKey, dedupe = true, runRequest }) => {
    if (!dedupe) {
      return await runRequest();
    }

    const scopeState = getScopeState(scopeKey);
    const inFlightRequest = scopeState.inFlight.get(requestKey);
    if (inFlightRequest) {
      return await inFlightRequest;
    }

    if (recentCompletionWindowMs > 0) {
      const now = Date.now();
      // prune expired entries so a long-lived scope's map stays bounded
      for (const [key, completedAt] of scopeState.recentCompletions) {
        if (now - completedAt > recentCompletionWindowMs) {
          scopeState.recentCompletions.delete(key);
        }
      }
      // survivors are within the window, so presence alone means suppress
      if (scopeState.recentCompletions.has(requestKey)) {
        return;
      }
    }

    const request = runRequest()
      .then(result => {
        // only successful completions arm the replay window; failures can retry immediately
        if (recentCompletionWindowMs > 0) {
          scopeState.recentCompletions.set(requestKey, Date.now());
        }
        return result;
      })
      .finally(() => {
        if (scopeState.inFlight.get(requestKey) === request) {
          scopeState.inFlight.delete(requestKey);
        }
      });

    scopeState.inFlight.set(requestKey, request);
    return await request;
  };

  // writes call this so an identical read issued right after a mutation refetches.
  // drop the whole scope entry rather than clearing its maps: a request already
  // in flight keeps its (now-orphaned) maps via closure, so its late completion
  // can't re-arm the replay window or coalesce with the fresh post-write read.
  runDeduped.invalidateScope = scopeKey => {
    scopeStateByKey.delete(scopeKey);
  };

  return runDeduped;
};

const REQUEST_REPLAY_DEDUPE_WINDOW_MS = 500;

// Shared deduper for redux-json-api reads; lives here rather than in apiActions so
// coreApiActions can invalidate on writes without a circular import.
export const runWithRequestDeduping = createRequestDeduper({
  recentCompletionWindowMs: REQUEST_REPLAY_DEDUPE_WINDOW_MS,
});
